"use client"

import { useEffect, useRef, useState } from "react"
import { AudioRecorderService } from "@/services/audio-recorder"
import { useMeetingRecordingStore } from "@/stores/use-meeting-recording-store"
import { LiveWaveform } from "@ui/live-waveform"
import { Button } from "@ui/shad"
import {
  CloudCheck,
  CloudUpload,
  LaptopMinimalCheck,
  Loader2,
  Pause,
  Play,
  Square,
  Trash2,
  TriangleAlert,
} from "lucide-react"
import { toast } from "sonner"

import { ConfirmDialog } from "@/components/dialogs"

interface RecordingVisualizerProps {
  meetingId?: string
  onClose?: () => void
}

export function RecordingVisualizer({ meetingId, onClose }: RecordingVisualizerProps) {
  const {
    isVisible,
    isRecording,
    isPaused,
    mediaRecorder,
    duration,
    startTime,
    pausedDuration,
    stopRecording,
    pauseRecording,
    resumeRecording,
    updateDuration,
    meetingId: storedMeetingId,
    reset,
    uploader,
    chunkSequence,
    pendingChunks,
    finishRecording,
    captureWarning,
  } = useMeetingRecordingStore()

  const [minSpinnerDone, setMinSpinnerDone] = useState(true)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [isDiscarding, setIsDiscarding] = useState(false)
  const spinnerTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const prevChunkSequenceRef = useRef(chunkSequence)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isFinishingRef = useRef(false)

  const activeMeetingId = meetingId || storedMeetingId
  useEffect(() => {
    if (chunkSequence > prevChunkSequenceRef.current && chunkSequence > 0) {
      setMinSpinnerDone(false)
      if (spinnerTimeoutRef.current) clearTimeout(spinnerTimeoutRef.current)
      spinnerTimeoutRef.current = setTimeout(() => setMinSpinnerDone(true), 1000)
    }
    prevChunkSequenceRef.current = chunkSequence

    return () => {
      if (spinnerTimeoutRef.current) clearTimeout(spinnerTimeoutRef.current)
    }
  }, [chunkSequence])

  const isLatestUploaded = chunkSequence > 0 && pendingChunks === 0
  const showSpinner = !minSpinnerDone
  const checkpointTime = isLatestUploaded ? "Saved" : "Saving audio"

  useEffect(() => {
    // Only run the timer when recording and not paused
    if (isRecording && !isPaused) {
      intervalRef.current = setInterval(() => {
        if (startTime) {
          // Calculate elapsed time since resume/start plus any previously paused duration
          const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000) + pausedDuration
          updateDuration(elapsed)
        }
      }, 1000)
    } else {
      // Clear the timer when paused or not recording
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isRecording, isPaused, startTime, pausedDuration, updateDuration])

  const handleStop = async () => {
    if (isFinishingRef.current || !activeMeetingId) return
    isFinishingRef.current = true
    try {
      await finishRecording()
      onClose?.()
    } finally {
      isFinishingRef.current = false
    }
  }

  const handlePauseResume = () => {
    if (isPaused) {
      resumeRecording()
    } else {
      pauseRecording()
    }
  }

  const handleFinishAndUpload = async () => {
    await handleStop()
  }

  const handleDiscard = async () => {
    if (isDiscarding) return
    setIsDiscarding(true)
    try {
      await stopRecording()
      await uploader?.cleanup()
      reset()
      toast.info("Recording discarded")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not discard this recording")
      await useMeetingRecordingStore.getState().restorePendingUpload()
    } finally {
      setIsDiscarding(false)
      setShowDiscardDialog(false)
    }
  }

  if (!isVisible) {
    return null
  }

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full items-center justify-center px-3 pt-2 pb-4">
        <div className="text-icon-muted w-full">
          <LiveWaveform
            active={isRecording && !isPaused}
            stream={mediaRecorder?.stream}
            height={64}
            barWidth={2}
            barGap={2}
            sensitivity={1.5}
            mode="static"
          />
        </div>
      </div>
      {captureWarning && (
        <div
          role="alert"
          className="bg-red-background text-red-foreground mx-4 mb-3 flex items-start gap-2 rounded-md px-3 py-2"
        >
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 text-sm font-medium wrap-break-word">
            {captureWarning === "stalled"
              ? "Audio capture has stalled. Check your microphone and keep this tab open. Some audio may be missing."
              : captureWarning === "unavailable"
                ? "Slant cannot verify microphone audio. Click here or check your browser's audio permissions."
                : "No microphone audio detected. Check that the selected microphone is connected and unmuted."}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between px-4 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span
              className={
                isPaused || captureWarning
                  ? "bg-icon-muted h-2 w-2 rounded-full"
                  : "bg-red-foreground h-2 w-2 animate-pulse rounded-full"
              }
            />
            <span className="text-secondary w-9 text-sm font-medium tabular-nums">
              {AudioRecorderService.formatDuration(duration)}
            </span>
          </div>
          {chunkSequence > 0 && (
            <div className="flex items-center gap-1.5">
              {showSpinner ? (
                <Loader2 className="text-icon-muted size-3 animate-spin" />
              ) : isLatestUploaded ? (
                <CloudCheck className="text-icon-muted size-3" />
              ) : (
                <LaptopMinimalCheck className="text-icon-muted size-3" />
              )}
              <span className="text-icon-muted text-xs tabular-nums">{checkpointTime}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {isRecording && !isPaused ? (
            <>
              <Button size="iconSm" variant="outline" onClick={handlePauseResume}>
                <Pause />
              </Button>
              <Button size="sm" variant="destructive" onClick={handleFinishAndUpload}>
                <Square fill="var(--destructive-foreground)" />
                Finish & upload
              </Button>
            </>
          ) : (
            <>
              <Button
                size="iconSm"
                variant="inverse"
                onClick={() => setShowDiscardDialog(true)}
                disabled={isDiscarding}
                title="Discard recording"
                className="text-background"
              >
                <Trash2 />
              </Button>
              <Button size="iconSm" variant="outline" onClick={handlePauseResume} title="Resume recording">
                <Play />
              </Button>
              <Button
                size="sm"
                variant="custom"
                onClick={handleStop}
                title="Save recording"
                className="bg-green-accent hover:bg-green-accent-hover text-white"
              >
                <CloudUpload />
                Finish & upload
              </Button>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={(open) => !isDiscarding && setShowDiscardDialog(open)}
        title="Discard recording?"
        description="This will delete the recorded audio. This cannot be undone."
        cancel={{ disabled: isDiscarding }}
        action={{ label: "Discard recording", onClick: handleDiscard, destructive: true, pending: isDiscarding }}
      />
    </div>
  )
}
